import { Cpu } from 'lucide-react';
import type { BlueprintArchitecture } from '@reflex/shared';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

/**
 * Per-architecture display metadata: the short label shown on the badge and
 * the full name surfaced via the native `title` on hover.
 */
const ARCH_DISPLAY: Record<BlueprintArchitecture, { label: string; title: string }> = {
  arm64: { label: 'arm64', title: 'Arm 64-bit' },
  x86_64: { label: 'x86_64', title: 'Intel/AMD 64-bit' },
};

export interface ArchBadgeProps {
  /** CPU architecture. `undefined` / missing is treated as the quiet default when `hideDefault`. */
  architecture: BlueprintArchitecture | undefined;
  /**
   * When true, only `arm64` renders a badge and `x86_64` / missing stay quiet
   * (blueprint rows and headers). When false, both values render so a status
   * surface can state what the box is actually running.
   */
  hideDefault?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Compact CPU-architecture badge. Use `hideDefault` on blueprint lists so the
 * common x86_64 case stays quiet; omit it on live status surfaces that should
 * always name the architecture.
 */
export function ArchBadge({
  architecture,
  hideDefault = false,
  className,
  'data-testid': dataTestId,
}: ArchBadgeProps) {
  if (hideDefault && architecture !== 'arm64') return null;
  if (!architecture) return null;

  const display = ARCH_DISPLAY[architecture];
  return (
    <Badge
      variant="outline"
      data-testid={dataTestId ?? 'arch-badge'}
      data-architecture={architecture}
      title={display.title}
      aria-label={`Architecture: ${display.title}`}
      className={cn('gap-1 px-1.5 py-0 text-3xs font-medium', className)}
    >
      <Cpu className="h-2.5 w-2.5" aria-hidden="true" />
      {display.label}
    </Badge>
  );
}
